import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Search, Building, Users, ChevronDown, ChevronRight, Mail, Phone, Plus, Loader, Unlink, Link, Eye } from 'lucide-react';
import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const MemberDirectory = ({ selectedLocation = 'All Locations' }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCompanies, setExpandedCompanies] = useState(new Set());
  const [members, setMembers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addingEmployeeTo, setAddingEmployeeTo] = useState(null);
  const [showAddCompany, setShowAddCompany] = useState(false);
  const [assigningOffice, setAssigningOffice] = useState(null);
  const [newEmployee, setNewEmployee] = useState({
    name: '',
    role: '',
    email: ''
  });
  const [newCompany, setNewCompany] = useState({
    company_name: '',
    location_id: '',
    primary_contact: {
      name: '',
      email: '',
      phone: ''
    },
    membership_type: 'Standard'
  });
  
  const { user } = useAuth();

  // Load members data
  useEffect(() => {
    loadMembers();
  }, [selectedLocation]);

  // Load locations and set default location for new company
  useEffect(() => {
    const loadLocations = async () => {
      try {
        const locationsData = await apiService.getLocations();
        setLocations(locationsData);
        
        // Set default location for new company if a specific location is selected
        if (selectedLocation !== 'All Locations') {
          const location = locationsData.find(loc => loc.name === selectedLocation);
          if (location) {
            setNewCompany(prev => ({ ...prev, location_id: location.id }));
          }
        }
      } catch (error) {
        console.error('Error loading locations:', error);
      }
    };
    loadLocations();
  }, [selectedLocation]);

  const loadMembers = async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = {};
      if (selectedLocation !== 'All Locations') {
        filters.location = selectedLocation;
      }
      
      // Load members and available offices
      const [membersData, officesData] = await Promise.all([
        apiService.getMembers(filters),
        apiService.getOffices(filters)
      ]);
      
      setMembers(membersData);
      setOffices(officesData);
    } catch (error) {
      console.error('Error loading members:', error);
      setError('Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  // Add employee to member
  const handleAddEmployee = async () => {
    if (!newEmployee.name || !newEmployee.email || !addingEmployeeTo) {
      setError('Name and email are required');
      return;
    }

    try {
      await apiService.addMemberEmployee(addingEmployeeTo, newEmployee);
      setNewEmployee({ name: '', role: '', email: '' });
      setAddingEmployeeTo(null);
      await loadMembers(); // Reload data
    } catch (error) {
      console.error('Error adding employee:', error);
      setError('Failed to add employee');
    }
  };

  // Add new company
  const handleAddCompany = async () => {
    if (!newCompany.company_name || !newCompany.location_id || !newCompany.primary_contact.name || !newCompany.primary_contact.email) {
      setError('Company name, location, contact name, and contact email are required');
      return;
    }

    try {
      await apiService.createMember(newCompany);
      setNewCompany({
        company_name: '',
        location_id: selectedLocation !== 'All Locations' ? newCompany.location_id : '',
        primary_contact: {
          name: '',
          email: '',
          phone: ''
        },
        membership_type: 'Standard'
      });
      setShowAddCompany(false);
      await loadMembers(); // Reload data
    } catch (error) {
      console.error('Error adding company:', error);
      setError('Failed to add company');
    }
  };

  // Assign office to member
  const handleAssignOffice = async (memberId, officeId) => {
    try {
      await apiService.assignMemberToOffice(memberId, officeId);
      await loadMembers(); // Reload data to show updated assignments
      setAssigningOffice(null);
    } catch (error) {
      console.error('Error assigning office:', error);
      setError('Failed to assign office to member');
    }
  };

  // Get available offices for assignment (vacant offices only)
  const getAvailableOffices = () => {
    return offices.filter(office => office.status === 'VACANT');
  };

  // Get assigned office for a member
  const getAssignedOffice = (memberId) => {
    return offices.find(office => office.tenant && members.find(m => m.id === memberId && m.office_id === office.id));
  };

  // Helper functions

  const toggleCompanyExpansion = (companyId) => {
    const newExpanded = new Set(expandedCompanies);
    if (newExpanded.has(companyId)) {
      newExpanded.delete(companyId);
    } else {
      newExpanded.add(companyId);
    }
    setExpandedCompanies(newExpanded);
  };

  const getMembershipColor = (type) => {
    switch (type) {
      case 'Premium': return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'Standard': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'Basic': return 'bg-gray-100 text-gray-800 border-gray-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const filteredMembers = members.filter(member => 
    member.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.primary_contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (member.employees || []).some(emp => 
      emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.email.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const totalEmployees = members.reduce((sum, company) => sum + (company.employees?.length || 0), 0);

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Member Directory</h1>
          <p className="text-gray-600 mt-2">Manage company members and employee contacts</p>
        </div>
        <Button 
          className="bg-blue-600 hover:bg-blue-700"
          onClick={() => setShowAddCompany(true)}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Company
        </Button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
          {error}
          <Button 
            variant="ghost" 
            size="sm" 
            className="ml-2 text-red-600 hover:text-red-700"
            onClick={() => setError(null)}
          >
            ×
          </Button>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-slate-600" />
            <p className="text-gray-600">Loading members...</p>
          </div>
        </div>
      ) : (
        <>
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardContent className="p-6 text-center">
                <Building className="w-8 h-8 mx-auto mb-2 text-blue-600" />
                <div className="text-2xl font-bold text-gray-900">{members.length}</div>
                <div className="text-sm text-gray-600">Member Companies</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 text-center">
                <Users className="w-8 h-8 mx-auto mb-2 text-green-600" />
                <div className="text-2xl font-bold text-gray-900">{totalEmployees}</div>
                <div className="text-sm text-gray-600">Total Employees</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 text-center">
                <Badge className="w-8 h-8 mx-auto mb-2 bg-purple-600 text-white flex items-center justify-center">
                  P
                </Badge>
                <div className="text-2xl font-bold text-gray-900">
                  {members.filter(m => m.membership_type === 'Premium').length}
                </div>
                <div className="text-sm text-gray-600">Premium Members</div>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* Search */}
      <Card>
        <CardContent className="p-6">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search companies, contacts, or employees..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardContent>
      </Card>

      {/* Member Companies */}
      <div className="space-y-4">
        {filteredMembers.map((company) => (
          <Card key={company.id} className="overflow-hidden">
            <Collapsible>
              <CollapsibleTrigger asChild>
                <CardHeader 
                  className="cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => toggleCompanyExpansion(company.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center">
                        {expandedCompanies.has(company.id) ? 
                          <ChevronDown className="w-5 h-5 text-gray-400" /> : 
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        }
                      </div>
                      <div>
                        <CardTitle className="text-lg">{company.company_name}</CardTitle>
                        <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                          <span>{company.primary_contact.name}</span>
                          <span>•</span>
                          <span>{company.employees?.length || 0} employees</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Badge className={getMembershipColor(company.membership_type)}>
                        {company.membership_type}
                      </Badge>
                      <div className="flex items-center space-x-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/members/${company.id}`);
                          }}
                          title="View Profile"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm">
                          <Mail className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm">
                          <Phone className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              
              <CollapsibleContent>
                <CardContent className="pt-0 border-t bg-gray-50">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-4">
                    {/* Contact Info */}
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3">Primary Contact</h4>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <Mail className="w-4 h-4 text-gray-400" />
                          <span className="text-sm">{company.primary_contact.email}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Phone className="w-4 h-4 text-gray-400" />
                          <span className="text-sm">{company.primary_contact.phone}</span>
                        </div>
                      </div>
                    </div>

                    {/* Office Assignment */}
                    <div className="col-span-1 lg:col-span-2 mb-4">
                      <h4 className="font-semibold text-gray-900 mb-3">Office Assignment</h4>
                      <div className="flex items-center space-x-3">
                        {company.office_id ? (
                          <div className="flex items-center space-x-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
                            <Building className="w-4 h-4 text-green-600" />
                            <span className="text-sm font-medium text-green-800">
                              {offices.find(o => o.id === company.office_id)?.office_number || 'Office Assigned'}
                            </span>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-green-600 hover:text-green-700"
                              onClick={() => handleAssignOffice(company.id, "")}
                            >
                              Unassign
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-500">No office assigned</span>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => setAssigningOffice(company.id)}
                            >
                              Assign Office
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Employees */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-gray-900">Employees</h4>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => setAddingEmployeeTo(company.id)}
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Add Employee
                        </Button>
                      </div>
                      <div className="space-y-2">
                        {(company.employees || []).map((employee, index) => (
                          <div key={employee.id || index} className="flex items-center space-x-3 p-2 rounded-lg bg-white">
                            <Avatar className="w-8 h-8">
                              <AvatarFallback className="text-xs">
                                {getInitials(employee.name)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{employee.name}</p>
                              <p className="text-xs text-gray-500 truncate">{employee.role}</p>
                            </div>
                            <Button variant="ghost" size="sm">
                              <Mail className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>
        ))}
      </div>

      {/* Add Employee Dialog */}
      <Dialog open={!!addingEmployeeTo} onOpenChange={() => setAddingEmployeeTo(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Employee</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="employeeName">Employee Name *</Label>
              <Input
                id="employeeName"
                value={newEmployee.name}
                onChange={(e) => setNewEmployee({...newEmployee, name: e.target.value})}
                placeholder="John Smith"
              />
            </div>
            <div>
              <Label htmlFor="employeeRole">Role</Label>
              <Input
                id="employeeRole"
                value={newEmployee.role}
                onChange={(e) => setNewEmployee({...newEmployee, role: e.target.value})}
                placeholder="Software Developer"
              />
            </div>
            <div>
              <Label htmlFor="employeeEmail">Email Address *</Label>
              <Input
                id="employeeEmail"
                type="email"
                value={newEmployee.email}
                onChange={(e) => setNewEmployee({...newEmployee, email: e.target.value})}
                placeholder="john.smith@company.com"
              />
            </div>
            <div className="flex space-x-2">
              <Button onClick={handleAddEmployee} className="flex-1">
                Add Employee
              </Button>
              <Button variant="outline" onClick={() => setAddingEmployeeTo(null)} className="flex-1">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Company Dialog */}
      <Dialog open={showAddCompany} onOpenChange={setShowAddCompany}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Company</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="companyName">Company Name *</Label>
              <Input
                id="companyName"
                value={newCompany.company_name}
                onChange={(e) => setNewCompany({...newCompany, company_name: e.target.value})}
                placeholder="Acme Corporation"
              />
            </div>
            <div>
              <Label htmlFor="companyLocation">Bond Collective Location *</Label>
              <Select 
                value={newCompany.location_id} 
                onValueChange={(value) => setNewCompany({...newCompany, location_id: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((location) => (
                    <SelectItem key={location.id} value={location.id}>
                      {location.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="membershipType">Membership Type</Label>
              <Select 
                value={newCompany.membership_type} 
                onValueChange={(value) => setNewCompany({...newCompany, membership_type: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Premium">Premium</SelectItem>
                  <SelectItem value="Standard">Standard</SelectItem>
                  <SelectItem value="Basic">Basic</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="border-t pt-4">
              <h4 className="font-semibold text-gray-900 mb-3">Primary Contact</h4>
              <div className="space-y-3">
                <div>
                  <Label htmlFor="contactName">Contact Name *</Label>
                  <Input
                    id="contactName"
                    value={newCompany.primary_contact.name}
                    onChange={(e) => setNewCompany({
                      ...newCompany, 
                      primary_contact: {...newCompany.primary_contact, name: e.target.value}
                    })}
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <Label htmlFor="contactEmail">Contact Email *</Label>
                  <Input
                    id="contactEmail"
                    type="email"
                    value={newCompany.primary_contact.email}
                    onChange={(e) => setNewCompany({
                      ...newCompany, 
                      primary_contact: {...newCompany.primary_contact, email: e.target.value}
                    })}
                    placeholder="john.smith@company.com"
                  />
                </div>
                <div>
                  <Label htmlFor="contactPhone">Contact Phone</Label>
                  <Input
                    id="contactPhone"
                    value={newCompany.primary_contact.phone}
                    onChange={(e) => setNewCompany({
                      ...newCompany, 
                      primary_contact: {...newCompany.primary_contact, phone: e.target.value}
                    })}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>
            </div>
            <div className="flex space-x-2">
              <Button onClick={handleAddCompany} className="flex-1">
                Add Company
              </Button>
              <Button variant="outline" onClick={() => setShowAddCompany(false)} className="flex-1">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Office Dialog */}
      <Dialog open={!!assigningOffice} onOpenChange={() => setAssigningOffice(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Office</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-600">
              Select an available office to assign to this company:
            </p>
            <div className="space-y-2">
              {getAvailableOffices().length === 0 ? (
                <p className="text-gray-500 italic">No vacant offices available for assignment</p>
              ) : (
                getAvailableOffices().map((office) => (
                  <Button
                    key={office.id}
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => handleAssignOffice(assigningOffice, office.id)}
                  >
                    <Building className="w-4 h-4 mr-2" />
                    Office {office.office_number} - {office.location_name}
                    <span className="ml-auto text-xs text-gray-500">
                      {office.desks} desks, Floor {office.floor}
                    </span>
                  </Button>
                ))
              )}
            </div>
            <div className="flex justify-end">
              <Button variant="outline" onClick={() => setAssigningOffice(null)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MemberDirectory;